import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

function sessionIdFor(context) {
  const key = `familia_bot_session_${context}`;
  try {
    let sid = localStorage.getItem(key);
    if (!sid) {
      sid = `fb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(key, sid);
    }
    return sid;
  } catch {
    return `fb-mem-${Math.random().toString(36).slice(2, 12)}`;
  }
}

const GREETING = {
  landing: "¡Hola! Soy Sude 👋 ¿Tienes dudas sobre el Plan Familias? Precio, cómo funciona, la demo… pregúntame lo que quieras.",
  panel: "¡Hola! Soy Sude 👋 ¿Te ayudo con algo del panel? Apuntar partidos, compartir la ficha, añadir otro jugador…",
};

export const FamiliaBot = ({ context = "landing" }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING[context] }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sidRef = useRef(null);
  if (!sidRef.current) sidRef.current = sessionIdFor(context);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const resp = await fetch(`${API}/familias/bot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, message: text, context }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "error");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          const payload = line.replace(/^data: /, "").trim();
          if (!payload || payload === "[DONE]") continue;
          try {
            const { t } = JSON.parse(payload);
            if (t) setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], text: copy[copy.length - 1].text + t };
              return copy;
            });
          } catch { /* skip malformed chunk */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: e.message !== "error" && e.message ? e.message : "No puedo responder ahora mismo. Escríbenos a sudefootball@sudeporte.com 🙏" };
        return copy;
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="familia-bot-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[80] w-14 h-14 rounded-full bg-primary text-white shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Abrir asistente"
        >
          <MessageCircle size={24} />
        </button>
      )}
      {open && (
        <div data-testid="familia-bot-panel" className="fixed bottom-5 right-5 z-[80] w-[92vw] max-w-sm bg-card border border-border shadow-2xl flex flex-col" style={{ height: "min(520px, 75vh)" }}>
          <div className="flex items-center justify-between px-4 h-12 border-b border-border bg-primary/10 shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-heading font-black uppercase text-sm">Sude · Asistente</span>
            </div>
            <button data-testid="familia-bot-close-btn" onClick={() => setOpen(false)} className="w-8 h-8 border border-border hover:border-primary flex items-center justify-center" aria-label="Cerrar">
              <X size={14} />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="familia-bot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-primary text-white" : "bg-background border border-border"
                }`}>
                  {m.text || <Loader2 size={14} className="animate-spin opacity-60" />}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-border flex gap-2 shrink-0">
            <input
              data-testid="familia-bot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Escribe tu duda…"
              maxLength={600}
              className="flex-1 h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary transition-colors"
            />
            <button
              data-testid="familia-bot-send-btn"
              onClick={send}
              disabled={busy || !input.trim()}
              className="w-10 h-10 bg-primary text-white flex items-center justify-center disabled:opacity-50"
              aria-label="Enviar"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
